import React, { useCallback, useEffect, useState } from "react";
import { Alert, Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import { MOBILE_MAX_WIDTH } from "../../constants/breakpoints";
import { getBarByIdApi } from "../../api/api";
import { useReservationFlow } from "./useReservationFlow";
import BarDetailDesktop from "./BarDetailDesktop";
import BarDetailMobile from "./BarDetailMobile";
import { SedePending } from "./components/Sucursales";
import { useSedeActiva } from "./components/useSedeActiva";
import { FALLBACK_IMAGE, extractBarPayload } from "./components/ReservaShared";
import "./reservas.css";

const normalizeBarDetail = (response) => {
  const payload = extractBarPayload(response);
  return {
    bar: payload?.bar || null,
    sucursales: Array.isArray(payload?.sucursales) ? payload.sucursales : [],
    casaCentral: payload?.casaCentral || null,
  };
};

/**
 * Owns the reservation flow of one bar. On a casa central `data.bar` is the chosen
 * sede (the shell remounts this per bar, which resets the selection), so the flow's
 * availability, hold and reservation calls all carry that sede's id.
 */
const BarBooking = ({ data, heroBar = null, sedes = null, isMobile }) => {
  const flow = useReservationFlow(data);
  return isMobile
    ? <BarDetailMobile flow={flow} heroBar={heroBar} sedes={sedes} />
    : <BarDetailDesktop flow={flow} heroBar={heroBar} sedes={sedes} />;
};

const BarDetail = () => {
  const { barId } = useParams();
  const navigate = useNavigate();
  const isMobile = useMediaQuery({ maxWidth: MOBILE_MAX_WIDTH });

  const [data, setData] = useState({ bar: null, sucursales: [], casaCentral: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        setLoading(true);
        setError("");
        const normalized = normalizeBarDetail(await getBarByIdApi(barId));
        if (!active) return;
        setData(normalized);
      } catch (err) {
        if (!active) return;
        setData({ bar: null, sucursales: [], casaCentral: null });
        setError(err?.response?.data?.message || "No pudimos cargar la información del bar.");
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [barId]);

  // Casa central: the sede in `?sucursal=` swaps the bar's bookable content in place.
  const fetchSedeDetail = useCallback(async (ref) => {
    const normalized = normalizeBarDetail(await getBarByIdApi(ref));
    return normalized.bar ? normalized : null;
  }, []);
  const { sedes, entry, status: sedeStatus } = useSedeActiva({ casa: data.bar, sucursales: data.sucursales, fetchDetail: fetchSedeDetail });

  if (loading) {
    return (
      <div className="reservas-root">
        <div className="wrap" style={{ textAlign: "center", padding: "80px 0" }}>
          <Spinner animation="border" />
        </div>
      </div>
    );
  }

  if (!data.bar) {
    return (
      <div className="reservas-root">
        <div className="wrap">
          <Alert variant="danger">{error || "No pudimos encontrar el bar."}</Alert>
          <button className="r-btn-soft" onClick={() => navigate("/reservas")}>
            Volver a reservas
          </button>
        </div>
      </div>
    );
  }

  // The chosen sede is still loading (or failed): casa central hero and Sucursales, no booking yet.
  if (sedeStatus) {
    return (
      <SedePending
        kind="reservas"
        hero={data.bar}
        heroImage={data.bar.image || data.bar.mobileimage}
        fallbackImage={FALLBACK_IMAGE}
        sucursales={data.sucursales}
        sedes={sedes}
        status={sedeStatus}
        isMobile={isMobile}
        panelTitle="Hacer una reserva"
      />
    );
  }

  // On a sede the booking runs on that sede's bar; the hero stays the casa central's.
  const current = entry ? { bar: entry.data.bar, sucursales: data.sucursales, casaCentral: null } : data;
  return (
    <BarBooking
      key={String(current.bar._id)}
      data={current}
      heroBar={entry ? data.bar : null}
      sedes={sedes}
      isMobile={isMobile}
    />
  );
};

export default BarDetail;
